import { Delete, ExpandMore } from '@mui/icons-material';
import type { SxProps } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { ProfileForm } from '@/components/forms/ProfileForm';
import { useSnackbar } from 'notistack';
import type { AuthUser, Partner } from '@/openapi';
import { UsersService } from '@/openapi';
import { useMutation } from '@tanstack/react-query';
import { buttonSize } from '@/util/styling';
import { nullsToUndefined } from '@/util/objects';
import { DeleteDialog } from './DeleteDialog';
import { useModal } from 'react-modal-hook';
import { useDeleteUserMutation } from '@/hooks/useDeleteUserMutation';
import { useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useUpdatePartnerMutation } from '@/hooks/useUpdatePartnerMutation';

export interface PartnerAccordionProps {
  partner: Partner;
  user: AuthUser;
}

export const PartnerAccordion: React.FC<PartnerAccordionProps> = ({
  partner,
  user,
}) => {
  const partnerMutatation = useUpdatePartnerMutation();
  const userMutation = useMutation({
    mutationFn: (authUser: AuthUser) => {
      return UsersService.usersServiceUpdateUser({
        path: {
          id: authUser.uid,
        },
        body: authUser,
      });
    },
  });
  const snackbar = useSnackbar();
  const updatePartnerAndUser = async (partner: Partner, user: AuthUser) => {
    try {
      const authUser: AuthUser = {
        uid: user.uid,
        displayName: `${partner.firstName} ${partner.lastName}`,
        email: partner.email,
        role: user.role,
      };
      await Promise.all([
        partnerMutatation.mutateAsync(nullsToUndefined(partner)),
        userMutation.mutateAsync(authUser),
      ]);

      snackbar.enqueueSnackbar('Partner updated successfully', {
        variant: 'success',
      });
    } catch {
      snackbar.enqueueSnackbar('Partner failed to update', {
        variant: 'error',
      });
    }
  };

  const [isHidden, setIsHidden] = useState(true);
  const theme = useTheme();
  const buttonStyle = buttonSize(theme);
  const buttonDisplay: SxProps = {
    visibility: isHidden ? 'hidden' : 'visible',
  };
  const deleteUserMutation = useDeleteUserMutation();

  const { isAdmin } = useAuth();

  const [showModal, hideModal] = useModal(() => (
    <DeleteDialog
      onConfirm={async () => {
        try {
          await Promise.all([deleteUserMutation.mutateAsync(user.uid)]);
          snackbar.enqueueSnackbar('User deleted successfully', {
            variant: 'success',
          });
          hideModal();
        } catch {
          snackbar.enqueueSnackbar('Failed to delete user', {
            variant: 'error',
          });
        }
      }}
      onCancel={hideModal}
      itemName={'partner'}
    />
  ));

  return (
    <Accordion
      key={partner.id}
      slotProps={{
        transition: { unmountOnExit: true },
      }}>
      <Stack
        direction="row"
        onMouseOver={() => setIsHidden(false)}
        onMouseOut={() => setIsHidden(true)}
        sx={{
          alignItems: 'center',
        }}>
        <AccordionSummary
          data-testid="partner-summary"
          expandIcon={<ExpandMore />}>
          <Grid
            container
            sx={{
              alignItems: 'center',
              width: '100%',
            }}>
            <Grid
              size={{
                xs: 8,
                md: 3,
              }}>
              <Typography variant="body1">
                {partner.firstName + ' ' + partner.lastName}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 4,
                md: 2,
              }}>
              <Typography variant="body1">{partner.initials}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3,
              }}>
              <Typography variant="body1">{partner.mobile}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3,
              }}>
              <Typography variant="body1">{partner.email}</Typography>
            </Grid>
          </Grid>
        </AccordionSummary>
        {isAdmin && (
          <Grid
            size={{
              xs: 2,
              md: 1,
            }}>
            <Button
              sx={{
                ...buttonStyle,
                ...buttonDisplay,
              }}
              onClick={(event) => {
                event.stopPropagation();
                showModal();
              }}>
              <Delete color="primary" />
            </Button>
          </Grid>
        )}
      </Stack>
      <AccordionDetails data-testid="partner-detail">
        <Grid
          size={{
            xs: 12,
            lg: 6,
          }}>
          <ProfileForm
            partner={partner}
            partnerUser={user}
            onSubmit={updatePartnerAndUser}
          />
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
